import { defineStore, signal } from 'kerfjs';

import type {
  ItemsResp,
  PulseResp,
  SessionResp,
  StateResp,
  SuggestSimilarResp,
  ThreadSummary,
  TopicSuggestion,
} from '../../api/schemas.js';
import { PROFILE_PAGE_COUNT } from '../../profiles.js';
import type { TunerState } from '../features/discovery/discover.js';
import type { ExportChoice } from '../features/export/export-url.js';
import {
  dismissPushNudge,
  type PushNudgeRecord,
  readPushNudgeRecord,
  writePushNudgeRecord,
} from '../features/notifications/re-engagement.js';

type NewsItem = ItemsResp['items'][number];

/**
 * One story's thread timeline as the pane knows it (NEWS-282).
 *
 * A three-state union rather than `items | null` plus flags: "asked and waiting",
 * "asked and failed" and "have it" are what the pane draws, and a shape that
 * cannot express them separately is a shape that renders an empty timeline while
 * a request is still in flight.
 */
export type ThreadPane =
  { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; items: NewsItem[]; size: number };

/** How many stories the feed reveals per "Show more" page (NEWS-62). */
export const FEED_PAGE = 100;

/** Sidebar ordering options (NEWS-63). */
export type TopicSort = 'alpha' | 'added' | 'recent' | 'category';
export const TOPIC_SORTS: readonly TopicSort[] = ['alpha', 'added', 'recent', 'category'];
export const TOPIC_SORT_LABELS: Record<TopicSort, string> = {
  alpha: 'A → Z',
  added: 'Recently Added',
  recent: 'Newest Stories',
  category: 'By Section',
};

/**
 * Steps of the first-run flow, in order (NEWS-78, extended NEWS-383/NEWS-394).
 *
 * `profiles` and `location` sit **before Topics**, and the order is not
 * arbitrary: that step creates topics and each fires its first check immediately
 * (FR-1.12) — anything asked afterwards would land too late to steer the very
 * first check it was meant to steer. There is no Source step any more (NM-12):
 * AI-provider configuration is environment-only, so there is nothing for the
 * user to set up here.
 */
export const ONBOARDING_STEPS = ['welcome', 'profiles', 'location', 'topics'] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/**
 * Suggested starter topics (NEWS-78) — broad enough that any of them returns
 * something on the first check, so the first feed is never empty.
 */
export const STARTER_TOPICS = [
  'Artificial intelligence',
  'Climate and energy',
  'Space exploration',
  'Global economy',
  'Public health',
  'Cybersecurity',
] as const;

/**
 * Where a result list came from, for the heading and for re-running it.
 *
 * Kept as the *request* rather than a rendered string so the pane can re-ask
 * without reconstructing what was asked — which is what a "try again" after a
 * provider failure needs.
 */
export type DiscoverSource =
  { kind: 'describe'; query: string } | { kind: 'section'; category: string; subcategory: string | null };

/** The discovery dialog's state (NEWS-126, `docs/24-topic-discovery.md`). */
export interface DiscoverState {
  /**
   * Which pane is showing. `browse` is the section grid, `results` the cards.
   * Both doors produce `results`, which is the point — neither is primary.
   */
  view: 'browse' | 'results';
  /** The section being drilled into, or null for the 11-tile grid. */
  section: string | null;
  /** Current contents of the free-text box. */
  query: string;
  loading: boolean;
  /**
   * A "More" request is in flight (NEWS-136).
   *
   * Separate from `loading` because the two replace different things: `loading`
   * swaps the whole pane for a status line, while asking for more must leave the
   * list the user is reading exactly where it is.
   */
  loadingMore: boolean;
  /**
   * The last "More" returned nothing new, so stop offering it.
   *
   * Every press is a billable call, so an exhausted seam has to be visible
   * rather than something the user discovers by pressing repeatedly.
   */
  exhausted: boolean;
  error: string | null;
  suggestions: TopicSuggestion[];
  /** What produced the current results, for the heading and for retrying. */
  source: DiscoverSource | null;
  /** True when the last answer came free from the cache (FR-24.15). */
  cached: boolean;
  /**
   * The keep/skip tuner (NEWS-127), or null when not tuning.
   *
   * Nested inside the discovery state rather than beside it: the tuner is a
   * *depth control* reached from a result list, never an entry point, and
   * closing the dialog must end it. A sibling field would allow a tuner that
   * outlives the list it came from, which is the shape variation B was rejected
   * for in the first place.
   */
  tuner: TunerState | null;
  /**
   * Names added during this dialog session.
   *
   * The card shows "Added" rather than vanishing: a row disappearing under the
   * cursor as you click down a list is how the *next* one gets clicked by
   * accident. They also can't simply be re-filtered out of `suggestions`,
   * because the server's exclusion list is what it was when the call was made.
   */
  added: string[];
}

export function emptyDiscover(): DiscoverState {
  return {
    view: 'browse',
    section: null,
    query: '',
    loading: false,
    loadingMore: false,
    exhausted: false,
    error: null,
    suggestions: [],
    source: null,
    cached: false,
    tuner: null,
    added: [],
  };
}

/**
 * A toast, and optionally the one thing it lets you do about it (NEWS-145).
 *
 * The action is a **topic id, not a callback**: state here is serialised into
 * the render and diffed, and a function in it would survive neither. There is
 * exactly one actionable toast in the app, so naming its subject beats a generic
 * action shape that would need a registry to interpret.
 */
export interface ToastState {
  message: string;
  /** Topic whose cleared stories are still restorable, or null for a plain toast. */
  undoTopicId: string | null;
}

/**
 * A topic the user has added but the server has not yet confirmed (NM-168). It
 * has no id yet — that comes from the create response — so it is keyed by a
 * client-minted `key` for the render and for removal when the create settles.
 */
export interface PendingTopic {
  key: string;
  name: string;
}

/**
 * The "did you mean?" result shown after a typed add (NM-148/154/179), rendered
 * as a dialog since NM-184. `pendingKey` is the optimistic placeholder already
 * showing in the rail for this add (NM-184) — the dialog's choice reconciles it:
 * "add anyway" reuses it, a correction/follow replaces it, dismiss removes it.
 */
export interface TopicSuggest {
  name: string;
  suggestions: SuggestSimilarResp['suggestions'];
  strongMatch: SuggestSimilarResp['strongMatch'];
  corrections: SuggestSimilarResp['corrections'];
  pendingKey: string;
}

/** The open capacity dialog (NM-201). See `AppState.capacityDialog`. */
export interface CapacityDialog {
  mode: 'disable' | 'enable';
  /** The kind whose cap is in play (`disable` mode); `enable` mode spans both. */
  kind: 'custom' | 'catalog';
  /** In `disable` mode, the topic(s) to turn on once room is freed. */
  enableIds: string[];
  /** Ticked topic ids — to disable in `disable` mode, to enable in `enable` mode. */
  picked: string[];
}

/** The open upgrade checkout (NM-225/226). See `AppState.checkout`. */
export interface CheckoutState {
  /** The tier being bought; null until the user picks one. */
  plan: string | null;
  /** True while the mock payment is in flight (disables the form). */
  submitting: boolean;
  /** The last decline/validation message, or null. */
  error: string | null;
}

/**
 * Which pending placeholders to still show (NM-168): drop any whose name a real
 * topic now carries. The create POST inserts the DB row *before* its one or two
 * AI classifications finish, so the 4-second `/api/state` poll can surface the
 * real row while the placeholder is still up — without this filter the two would
 * both show for a beat. The match is trimmed + case-insensitive, since the stored
 * name can be normalized from what was typed (and "follow an existing subject"
 * resolves to a name that may differ in case).
 */
export function visiblePendingTopics(
  pending: readonly PendingTopic[],
  topics: readonly { name: string }[],
): PendingTopic[] {
  const realNames = new Set(topics.map((t) => t.name.trim().toLowerCase()));
  return pending.filter((p) => !realNames.has(p.name.trim().toLowerCase()));
}

/**
 * Preserve object identity for unchanged rows across a poll (NM-214). Returns
 * `next` with any element whose `id`-matched predecessor in `prev` is structurally
 * identical replaced by that predecessor — so a keyed `each()` sees a stable object
 * reference and skips the row instead of re-reconciling it on every 4-second poll,
 * which is what let an idle app churn the sidebar's DOM. A cheap `JSON.stringify`
 * decides "unchanged": these are flat data rows parsed from the same server JSON, so
 * their key order is stable and the compare is exact.
 */
export function carryIdentity<T extends { id: string }>(prev: readonly T[], next: readonly T[]): T[] {
  if (prev.length === 0) return [...next];
  const prevById = new Map(prev.map((p) => [p.id, p]));
  return next.map((n) => {
    const p = prevById.get(n.id);
    return p !== undefined && JSON.stringify(p) === JSON.stringify(n) ? p : n;
  });
}

export interface AppState {
  /**
   * The `/api/session` response, or `null` while the boot fetch is in flight
   * (NM-20, phase 3). The top-level gate switches on `sessionGateState(session)`
   * — signed out / unregistered / blocked / active — and the app proper mounts
   * only in the `active` case. `null` renders a brief loading screen.
   */
  session: SessionResp | null;
  /**
   * A message from the last sign-in attempt (a blocked pop-up), shown on the
   * sign-in gate. Ephemeral; cleared when a fresh attempt starts.
   */
  signInError: string | null;
  loaded: boolean;
  /**
   * Whether the feed page (`/api/items`) has answered at least once (NM-249).
   *
   * Separate from `loaded`, which tracks `/api/state` (topics, plan): the feed
   * is a **second, independent** fetch, so `loaded` can be true — topics known —
   * while `feedItems` is still the empty initial value because the items request
   * has not returned yet. Gating the "No stories yet" empty state on `loaded`
   * alone therefore flashed it for an account that *does* have stories, in the
   * gap between the two responses. This is what the feed empty state waits on.
   */
  feedLoaded: boolean;
  /** Last error shown in the banner, or null. */
  error: string | null;
  topics: StateResp['topics'];
  /**
   * Topics the user just added, shown as pending rows *before* the server has
   * confirmed them (NM-168). Creating a topic is a POST that runs one or two
   * synchronous AI classifications before it returns, so the real row used to
   * take several seconds to appear and the add read as broken. Each pending
   * entry renders a muted, non-interactive placeholder in the rail until the
   * real topic arrives; the render drops any whose name a real topic now
   * matches (a poll can surface the row mid-POST), and `submitAddTopic` removes
   * it by `key` when the create settles — so it can never linger.
   */
  pendingTopics: PendingTopic[];
  /** The current feed page, fetched from `/api/items` for the active view (NEWS-76). */
  feedItems: NewsItem[];
  /** Total stories matching the active view, from the server — drives "Show more". */
  feedTotal: number;
  /**
   * Thread shape per story id for the current feed page (NEWS-282), keyed by
   * **story** id: position, size, and when the subject first appeared.
   *
   * Arrives with the page rather than being fetched per card — three numbers a
   * story wide, and only for stories whose thread holds more than one, so the
   * ordinary feed carries an empty map. It decides whether a card has a thread
   * badge (NEWS-283) *and* whether expanding it has anything to fetch.
   *
   * `| undefined` in the value type because this is read **by key** and most
   * keys are absent — the same shape as `todayByTopic` in `RowRenderState`, and
   * for the same reason: a lookup that cannot express a miss lies about it.
   */
  threads: Record<string, ThreadSummary | undefined>;
  /**
   * The timeline behind each story whose pane has been opened this session
   * (NEWS-282), keyed by story id.
   *
   * A cache, deliberately: collapsing and re-opening a card must not re-ask the
   * server for something it already answered. `size` is what it was fetched at,
   * so a thread that has since grown — the feed poll would show a bigger badge —
   * is refetched rather than served stale. An `error` entry is never reused, so
   * a failure is retried by re-opening the card as well as by the retry button.
   */
  threadPanes: Record<string, ThreadPane | undefined>;
  /**
   * Whether the open pane is showing its whole thread rather than the last
   * `THREAD_ROW_CAP` rows (NEWS-282).
   *
   * One flag rather than a set, because the feed is an accordion: only one pane
   * can be open, so "showing all" can only be true of that one. Opening any card
   * resets it — a cap the previous card's reader lifted is not a preference.
   */
  threadShowAll: boolean;
  /** Off-topic count per topic, for the "Review Flagged (N)" badge (NEWS-76). */
  flaggedByTopic: Record<string, number>;
  /** Stories found today per topic, for the sidebar badge (NEWS-242). */
  todayByTopic: Record<string, number>;
  /** Newest story's `foundAt` per topic, for the most-recent sort (NEWS-241). */
  newestItemAtByTopic: Record<string, string>;
  /** Seven daily story counts per topic, used only for the rail sparklines. */
  pulseSparklines: Record<string, number[] | undefined>;
  /** Thirty-day compact summary for the one soloed topic, if there is one. */
  topicPulse: PulseResp | null;
  /** Thirty-day compact summary for the active taxonomy filter. */
  categoryPulse: PulseResp | null;
  /** Full drill-down dialog; null means closed. */
  pulseDetail: PulseResp | null;
  pulseDetailOpen: boolean;
  pulseLoading: boolean;
  /** The period the drill-down asked for — what its period control shows. */
  pulseDays: 7 | 30 | 90;
  /**
   * The drill-down request whose answer the dialog is waiting for, or null when
   * none has been made (NM-HFXEYX). An answer for any other scope or period is
   * stale — a slower earlier request — and is dropped rather than shown.
   */
  pulsePending: PulseRequest | null;
  settings: StateResp['settings'];
  runs: StateResp['runs'];
  checking: string[];
  /** App version, for the diagnostics bundle (NEWS-88). */
  appVersion: string;
  /**
   * The AI backend the environment resolved (NM-12), for the read-only status
   * line on the App tab. Provider configuration is environment-only — no picker,
   * no persisted setting — so this is the single source of truth for which
   * backend is active and whether it can run a check now. Arrives on every
   * `/api/state` poll.
   */
  source: StateResp['source'];
  /** Which settings tab is showing (NEWS-118). Resets to the first on reopen. */
  settingsTab: 'data' | 'app';
  /**
   * The export dialog (NEWS-158, NEWS-160), or null when closed.
   *
   * Scope, topic and format are held together because the export is one choice
   * made in parts — every combination is valid, which is the point of replacing
   * the three fixed buttons that offered three of them.
   */
  export: ExportChoice | null;
  /**
   * True while an export is being fetched + saved (NM-101). The export controls
   * disable and read "Exporting…" so a generated export gives progress + result
   * feedback (a success/failure toast) instead of a silent native download.
   */
  exporting: boolean;
  /** Topic discovery (NEWS-126), or null when the dialog is closed. */
  discover: DiscoverState | null;
  /** Whether the settings dialog is open. */
  settingsOpen: boolean;
  /** Whether the Add Topic dialog is open (NM-ZJT0H7) — the add flow's only home. */
  addTopicOpen: boolean;
  /**
   * First-run flow (NEWS-78): the step being shown, or null when closed.
   *
   * `'auto'` is the not-yet-decided state — it opens itself once state and
   * providers have both loaded and the app turns out to be unusable. Without
   * that tri-state the wizard flashes open on every reload before the provider
   * probe has answered.
   */
  onboarding: 'auto' | OnboardingStep | null;
  /**
   * An unread notice that a database was set aside as unreadable (NEWS-340).
   * Server-owned: dismissing deletes the row, so unlike `dismissedRunId` there
   * is no client-side "seen" flag to keep in step with a poll.
   */
  quarantine: { backupPath: string; at: string } | null;
  /**
   * A stable id for the database behind this server (NEWS-423). `''` until the
   * first `/api/state` answers, and `''` from a server too old to send it —
   * `readOnboardingSeen` treats both as "cannot tell".
   */
  installId: string;
  /**
   * Maintenance mode's public face (NM-75): whether an operator has posted a
   * maintenance banner and its text. Server-owned, arrives on every `/api/state`
   * poll; the reader renders a banner when `enabled` and the message is non-empty.
   */
  maintenance: StateResp['maintenance'];
  /** The reader's plan + topic usage (NM-135); null on the non-enforcing default-account path. */
  plan: StateResp['plan'];
  /** Self-serve upgrade availability + the tier table (NM-225/226). */
  billing: StateResp['billing'];
  /**
   * The RTDB change-signal channel (NM-250), or null on the long-poll deploy.
   * Read once after the first load to pick the live-update transport.
   */
  signal: StateResp['signal'];
  /**
   * The Web Push VAPID public key (NM-251), or null when push is not configured.
   * The notify toggle subscribes with it when present.
   */
  pushPublicKey: StateResp['pushPublicKey'];
  /** The open upgrade checkout (NM-225/226); null when closed. */
  checkout: CheckoutState | null;
  /** The pending "did you mean?" overlap steer for a typed add (NM-148/153); null when none. `strongMatch` is the AI meaning-judge's high-confidence "same subject" flag (NM-154). */
  topicSuggest: TopicSuggest | null;
  /**
   * Id of the topic whose disambiguation **sense picker** is open (NM-163), or
   * null. Ephemeral, like every dialog. Opened when an ambiguous topic is added
   * (the immediate prompt) or when its "needs attention" row is clicked; the
   * picker's options come from that topic's `senseOptions` in `topics`.
   */
  sensePickTopicId: string | null;
  /**
   * Topics ticked in the onboarding flow, before they're created.
   *
   * Holds both the static starter names and anything picked from the suggestions
   * below them (NEWS-128) — one list, so the running count and the "picking none
   * is fine" wording keep working unchanged, and one toggle handler serves both.
   */
  onboardingTopics: string[];
  /**
   * The topics that existed, by name, when onboarding opened (NEWS-146, NM-91).
   *
   * The Topics step now opens the real discovery dialog, whose Add creates
   * immediately, so the step needs to report topics that already exist as well as
   * ones still only ticked. The difference between `topics.length` and this
   * snapshot's length is how many were *added this run*. A baseline rather than an
   * empty list because Settings can reopen onboarding for someone who already has
   * topics — and NM-91 needs their **names**, not just a count, so the running
   * line can say "You already watch Solid-state batteries" instead of "None yet".
   */
  onboardingExistingNames: string[];
  /**
   * Profile ids ticked so far in the picker (NEWS-383), across all three pages.
   *
   * Seeded from the stored setting when the step is entered, so reopening the
   * guide from Settings shows previous picks already ticked rather than a blank
   * grid that misrepresents what is saved.
   */
  onboardingProfiles: string[];
  /**
   * The profile ids that were already saved when the guide opened (NM-248).
   *
   * Snapshotted on entry, exactly like `onboardingExistingNames` snapshots the
   * pre-existing topics. On Finish, only profiles selected *this run* — those in
   * the saved set but not in this snapshot — derive new topics, so re-running
   * setup to change interests adds topics for the newly-picked ones and not the
   * whole interest set the user has accumulated. Empty on a genuine first run, so
   * every picked profile counts as new there.
   */
  onboardingProfilesAtOpen: string[];
  /**
   * Which of the three profile pages is showing (NEWS-383).
   *
   * The step owns its own paging: Continue advances the *page* until the last
   * one, then advances the step. One button, and the wizard's dots keep counting
   * steps rather than suddenly counting pages.
   */
  onboardingProfilePage: number;
  /** How the topics sidebar is ordered (per-device, NEWS-63). Default A→Z. */
  topicSort: TopicSort;
  /** Currently selected topic ids. Selection is transient — never persisted. */
  selectedTopicIds: string[];
  /** When true, the feed shows only bookmarked stories (NEWS-42). Ephemeral. */
  savedFilter: boolean;
  /** Live feed search query (NEWS-60). Ephemeral, composes with Solo/Saved. */
  searchQuery: string;
  /** How many stories the feed currently shows; grows by `FEED_PAGE` (NEWS-62). */
  feedLimit: number;
  /**
   * Topics that are solo'd: when non-empty, the feed shows only their stories.
   *
   * Deliberately in-memory and cleared on reload. A solo that survived a
   * restart would silently hide news days later, and "the app stopped finding
   * anything" is a much worse failure than re-applying a filter.
   */
  soloTopicIds: string[];
  /**
   * Filter-bar selection (NEWS-97): a category slug, `'uncategorized'`, or null
   * for "All". `subcategory` is the second-row selection within it.
   *
   * **Ephemeral, like Solo and for the same reason** (`docs/3-ui.md`): a filter
   * that quietly survived a restart would hide news days later, and "the app
   * stopped finding anything" is a far worse failure than re-applying a filter.
   * The sidebar collapse and topic sort *are* persisted — they change how the
   * app looks, not what it is willing to show you.
   */
  categoryFilter: { category: string; subcategory: string | null } | null;
  /**
   * Open topic context menu, positioned in viewport coordinates. `overflow` is
   * set when it is the mobile action bar's "…" popup (NM-240) — the same menu with
   * the two actions already on the bar (Disable/Enable, Edit) hidden, so it shows
   * only the remaining ones (Solo, Review Flagged, Delete).
   */
  /**
   * Whether the sidebar's collapsed **Disabled** group is expanded (NM-201). A
   * disabled subject (over the plan cap, or turned off) is parked here with no
   * articles (NM-198); the group is collapsed by default so it stays out of the
   * way. Ephemeral view state, like the sort and the selection.
   */
  disabledGroupOpen: boolean;
  /**
   * The open **capacity** dialog (NM-201, docs/48 FR-48.6), or null. Two modes on
   * one dialog:
   *  - `disable`: the reader tried to enable `enableIds` but is over the `kind`
   *    cap; they pick currently-enabled topics of that kind to disable, then the
   *    atomic swap commits "enable these, disable those".
   *  - `enable`: the upgrade prompt — free capacity opened up (a plan upgrade), so
   *    they pick disabled topics to turn back on, up to the free room.
   * `picked` is the ticked set (topics to disable in `disable` mode, to enable in
   * `enable` mode); held in the store so the poll can't reset the ticks and the
   * over/under counter binds to it.
   */
  capacityDialog: CapacityDialog | null;
  /** Open story context menu (bookmark / share / flag), viewport coords (NEWS-61). */
  /**
   * Id of the story whose detail pane is open, or null (NEWS-281).
   *
   * **One at a time — an accordion, not a set.** Two reasons, both about the
   * feed rather than about simplicity: cards lay out in a CSS grid whose rows
   * stretch every card to the tallest on the line (FR-3.37), so a second open
   * pane grows a row that already grew; and the pane is *reading* surface, which
   * is a thing you do to one story at a time. A set would also need a rule for
   * what clears it, and "the story I am reading" clears itself.
   *
   * Ephemeral, like Solo and the filters (`docs/3-ui.md`): every action that
   * replaces the list collapses it, because a pane pinned to a story that is no
   * longer on screen is state nobody can see or dismiss.
   */
  expandedItemId: string | null;
  /**
   * Stories flagged off-topic **this session** (NEWS-61), full data kept so
   * they can be merged (collapsed) into the server's normal-view page, which
   * now excludes them (NEWS-76). A just-flagged story stays visible as a dimmed
   * one-liner so a mistake is undoable; on reload this is empty, so flagged
   * stories are simply gone.
   */
  recentlyFlaggedItems: NewsItem[];
  /**
   * When non-empty, the feed is in "review flagged" mode (NEWS-61): it shows
   * ONLY the off-topic stories for these topics. Ephemeral; a banner exits it.
   */
  reviewTopicIds: string[];
  /**
   * Open confirmation dialog, or null. In-app rather than `window.confirm` so the
   * confirm is styled, accessible, and testable through the real UI.
   */
  /**
   * `title` gives a danger confirm an emphasized header (a warning icon + heading,
   * NM-101); `holdSeconds` arms a countdown that keeps the confirm button disabled
   * until `remaining` reaches 0, so an irreversible bulk delete cannot be blown
   * through instantly. `holdSeconds: 0` (the default) means no countdown.
   */
  confirm: {
    message: string;
    title: string | null;
    confirmLabel: string;
    danger: boolean;
    holdSeconds: number;
    remaining: number;
  } | null;
  /** Id of the topic being renamed (NEWS-139), or null. Ephemeral, like every dialog. */
  renameTopicId: string | null;
  /**
   * The section chosen in the edit dialog but not yet saved (NEWS-407).
   *
   * Held here rather than read off the topic, because the dialog has to render a
   * choice the server has not been told about — and because changing the section
   * changes which subjects exist, which is a re-render the DOM cannot drive on
   * its own. `null` is a real value meaning "let Newsmonger decide"; the pair is
   * seeded from the topic when the dialog opens.
   */
  renameCategory: string | null;
  renameSubcategory: string | null;
  /**
   * How many stories the topic being renamed has, or null while it is unknown.
   *
   * Fetched when the dialog opens rather than carried on `/api/state`: it is a
   * `GROUP BY` over every story, and `/api/state` is polled every four seconds
   * by every open client. NEWS-75/76 slimmed that payload deliberately, and
   * putting a growing aggregate back on it measurably slowed the settings round
   * trip under a full test suite. The dialog needs one number, once.
   */
  renameItemCount: number | null;
  /** True when the user tried to enable notifications but permission was refused. */
  notifyPermissionDenied: boolean;
  /**
   * Transient one-line notice shown at the bottom of the screen, or null.
   * In-app rather than `window.alert`, used to confirm a share landed on the
   * clipboard when there's no OS share sheet (NEWS-43).
   */
  toast: ToastState | null;
  /**
   * Id of the failed check-run whose warning banner the user dismissed. The
   * warning is derived from the runs list (server state), not a piece of
   * dismissable state — so dismissal is remembered by run id. A *different*
   * later failure has a new id and shows again, which is what you want.
   */
  dismissedRunId: string | null;
  /**
   * The device's push-nudge snooze record (NM-272, docs/51), or null when it has
   * never been dismissed. Read from localStorage once at boot; `snoozePushNudge`
   * advances the 15 → 30 → 90 → never schedule and writes it back.
   */
  pushNudge: PushNudgeRecord | null;
  /**
   * The latest spoken line for the polite check-progress live region (NM-92).
   *
   * Ephemeral and screen-reader-only: a topic starting a check says so, and one
   * finishing reports what it found ("Fusion power: 2 new stories"). Computed by
   * `noteCheckState` from successive `/api/state` polls, so it changes only on a
   * real transition — an unchanged string is a no-op for the live region.
   */
  checkAnnouncement: string;
}

/**
 * Persisted per device rather than in the store: how you've sized your own
 * window is a view preference, not something that belongs in the shared data
 * file alongside topics and stories.
 */
/**
 * Whether the first-run flow has been dismissed (NEWS-78). Per-device, like the
 * other view preferences: it records what *this* browser has already shown, not
 * anything about the account's data.
 *
 * **It stores the install id it was dismissed for, not `'1'`** (NEWS-423). The
 * flag being a bare boolean meant deleting `~/.newsmonger` — the gesture people
 * reach for to start over — left it standing, so the desktop app had no factory
 * reset: every topic and setting gone, and the app still behaving as though it
 * had already introduced itself.
 *
 * Moving the flag to the server would have fixed that and broken the property
 * this comment opens with, since two browsers against one server would then
 * share a single dismissal. Naming the install keeps both: a new database is a
 * new id, so the guide returns; the same database is the same id, so it stays
 * shut; and each browser still answers for itself.
 *
 * **The pre-NEWS-423 value `'1'` is deliberately not honoured.** Reading it as
 * "seen" was the first attempt at this change, and it defeated the fix for the
 * only person it was for: the report came from someone whose webview already
 * held a `'1'`, so honouring it would have left them with exactly the behaviour
 * they filed. It also bought nothing — the case it was meant to spare is an
 * existing user on upgrade, and they have topics, which is already the whole
 * auto-open test (FR-20.1).
 */
const ONBOARDING_KEY = 'news:onboarding-seen';

/**
 * Per-account, not per-device (NM-39). The flag lives in `localStorage`, which is
 * per-browser and shared by every account that signs in there — so keying it on
 * the install alone meant a brand-new account arriving on a browser that had
 * already onboarded some *other* account (a common thing while testing personas)
 * got no setup guide at all. Suffixing the key with the account id gives each
 * account its own flag; the stored value stays the `installId`, so a database
 * change still invalidates it. An empty `accountId` is treated like an empty
 * `installId` below — "cannot tell", i.e. seen.
 */
function onboardingKey(accountId: string): string {
  return `${ONBOARDING_KEY}:${accountId}`;
}

/**
 * `installId` empty means the server did not say — an older build, or a state
 * response cached across an upgrade. That reads as **seen**, because the wrong
 * guess in that direction is a missing prompt rather than a wizard thrown over
 * an established user's feed. An empty `accountId` (no signed-in account yet)
 * reads the same way.
 */
export function readOnboardingSeen(installId: string, accountId: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  if (installId === '' || accountId === '') return true;
  try {
    return localStorage.getItem(onboardingKey(accountId)) === installId;
  } catch {
    return false;
  }
}

export function writeOnboardingSeen(installId: string, accountId: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    // Nothing to record against an install or account we cannot name: a
    // placeholder would be a dismissal that no id ever matches, so it could only
    // read as unseen.
    if (installId === '' || accountId === '') return;
    localStorage.setItem(onboardingKey(accountId), installId);
  } catch {
    // private mode / storage disabled — the flow just reappears next launch
  }
}

/**
 * Test-only auto-open suppression (NM-222). The guide now opens whenever there
 * are no topics, every session ({@link shouldOpenOnboarding}); the E2E suite runs
 * many specs at zero topics and must not have the wizard thrown over them, so its
 * fixture seeds this flag. **Real users never set it** — the app only reads it,
 * and no product path writes it. It exists because the old suppression (a matching
 * per-install `onboarding-seen`) no longer holds the wizard shut.
 */
const ONBOARDING_AUTO_SUPPRESS_KEY = 'news:onboarding-suppress-auto';
export function readOnboardingAutoOpenSuppressed(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(ONBOARDING_AUTO_SUPPRESS_KEY) === '1';
  } catch {
    return false;
  }
}

const SIDEBAR_KEY = 'news:sidebar-collapsed';
/**
 * The Workbench's overlay breakpoint (kerf UI `responsiveOverlayAt: 'narrow'`,
 * a 704px container). At or below it the Watching rail presents as an overlay
 * drawer instead of a column.
 */
export const RAIL_OVERLAY_WIDTH = 704;

/** Whether the Watching rail presents as an overlay at the current window width. */
export function railIsOverlay(): boolean {
  try {
    return typeof window !== 'undefined' && window.innerWidth <= RAIL_OVERLAY_WIDTH;
  } catch {
    return false;
  }
}

function readRailCollapsed(): boolean {
  // Seeded collapsed where the rail is an overlay (NM-102, and kerf UI's rule
  // that a first render at an overlay breakpoint has no overlay open) — the
  // drawer opens only from its toggle. `wireWorkbench` keeps it that way across
  // breakpoint crossings and restores the inline state when the window widens.
  if (railIsOverlay()) return true;
  // Guarded so importing this module outside a browser can't throw.
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false; // private mode / storage disabled
  }
}

/**
 * Whether the Watching rail is collapsed (kerf UI shell, NM-D5TYNV).
 *
 * A signal rather than store state because `wireWorkbench` owns the overlay's
 * transient half of it — it writes this signal when an overlay is dismissed by
 * Escape or an outside press, and when the overlay breakpoint starts or stops
 * applying. The app writes it from the rail toggles via `setRailCollapsed`.
 */
export const railCollapsed = signal(readRailCollapsed());

/**
 * Collapse or expand the rail from a user gesture. The choice is remembered
 * per device only while the rail is an inline column: an overlay drawer is
 * transient (it always starts closed), so opening one must not make the rail
 * start collapsed — or expanded — on the next wide load.
 */
export function setRailCollapsed(collapsed: boolean): void {
  railCollapsed.value = collapsed;
  if (railIsOverlay() || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0');
  } catch {
    // Storage unavailable — the toggle still works for this session.
  }
}

const TOPIC_SORT_KEY = 'news:topic-sort';

function readTopicSort(): TopicSort {
  if (typeof localStorage === 'undefined') return 'alpha';
  try {
    const v = localStorage.getItem(TOPIC_SORT_KEY);
    return v !== null && (TOPIC_SORTS as readonly string[]).includes(v) ? (v as TopicSort) : 'alpha';
  } catch {
    return 'alpha';
  }
}

/**
 * The dismissed failed-run id is persisted (NEWS-41): the failure warning is
 * derived from server state that outlives a page reload, so an in-memory
 * dismissal meant relaunching the app resurrected a warning the user had
 * already closed. A *new* failure has a new id and still shows.
 */
const DISMISSED_RUN_KEY = 'news:dismissed-run';

function readDismissedRunId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(DISMISSED_RUN_KEY);
  } catch {
    return null;
  }
}

function writeDismissedRunId(id: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(DISMISSED_RUN_KEY, id);
  } catch {
    // private mode / storage disabled — dismissal just won't persist
  }
}

/**
 * Bumped once per completed `/api/state` poll (NM-230).
 *
 * NM-230 stops an *unchanged* poll from re-applying state, so the app's tree no
 * longer re-renders every four seconds — which is the point (a hand-edited DOM
 * node survives, an idle tab does no work). But the `<select>`/radio dirty-flag
 * re-sync (`syncSelects`/`syncRadios`, NEWS-238 / FR-3.5a) rode on that
 * every-poll render: a server-bound control the user has moved without it
 * persisting (a failed PATCH, a synthetic dirty flag) is corrected by the
 * property-vs-attribute pass, and that pass must still run each poll even when
 * the tree does not. This signal is that heartbeat: the sync effect reads it, so
 * it fires per poll independently of whether state changed. It touches only form
 * controls whose property drifted from their attribute — never the rail nodes a
 * developer edits — so it re-asserts the theme radio without undoing NM-230.
 */
export const pollTick = signal(0);

/** A drill-down request: the scope to analyse and the period (NM-HFXEYX). */
export interface PulseRequest {
  kind: 'topic' | 'category';
  id: string;
  subcategory?: string | null;
  days: 7 | 30 | 90;
}

function normalizePulseRequest(request: PulseRequest): PulseRequest & { subcategory: string | null } {
  return { ...request, subcategory: request.subcategory ?? null };
}

/** Whether `request` is the drill-down request still being waited for. */
export function isPendingPulse(request: PulseRequest): boolean {
  const pending = appStore.state.value.pulsePending;
  return pending !== null && pending.days === request.days && sameScope(pending, request);
}

function sameScope(a: Omit<PulseRequest, 'days'>, b: Omit<PulseRequest, 'days'>): boolean {
  return a.kind === b.kind && a.id === b.id && (a.subcategory ?? null) === (b.subcategory ?? null);
}

/** Whether a pulse answer's scope is the scope a request named. */
function samePulseScope(scope: PulseResp['scope'], request: PulseRequest): boolean {
  return sameScope(scope, request);
}

export const appStore = defineStore({
  initial: (): AppState => ({
    session: null,
    signInError: null,
    loaded: false,
    feedLoaded: false,
    error: null,
    topics: [],
    pendingTopics: [],
    feedItems: [],
    feedTotal: 0,
    threads: {},
    threadPanes: {},
    threadShowAll: false,
    flaggedByTopic: {},
    todayByTopic: {},
    newestItemAtByTopic: {},
    pulseSparklines: {},
    topicPulse: null,
    categoryPulse: null,
    pulseDetail: null,
    pulseDetailOpen: false,
    pulseLoading: false,
    pulseDays: 30,
    pulsePending: null,
    settings: {
      location: '',
      profiles: [],
      notifyOnNewItems: false,
      theme: 'auto',
    },
    runs: [],
    checking: [],
    appVersion: '',
    source: {
      provider: '',
      model: '',
      available: false,
      detail: 'No AI provider configured — set OPENAI_API_KEY or ANTHROPIC_API_KEY',
      fallback: false,
    },
    settingsTab: 'app',
    export: null,
    exporting: false,
    discover: null,
    settingsOpen: false,
    addTopicOpen: false,
    onboarding: 'auto',
    quarantine: null,
    maintenance: { enabled: false, message: '' },
    plan: null,
    billing: { enabled: false, plans: [] },
    signal: null,
    pushPublicKey: null,
    checkout: null,
    topicSuggest: null,
    sensePickTopicId: null,
    disabledGroupOpen: false,
    capacityDialog: null,
    installId: '',
    onboardingTopics: [],
    onboardingExistingNames: [],
    onboardingProfiles: [],
    onboardingProfilesAtOpen: [],
    onboardingProfilePage: 0,
    topicSort: readTopicSort(),
    selectedTopicIds: [],
    savedFilter: false,
    searchQuery: '',
    feedLimit: FEED_PAGE,
    soloTopicIds: [],
    categoryFilter: null,
    expandedItemId: null,
    recentlyFlaggedItems: [],
    reviewTopicIds: [],
    confirm: null,
    renameTopicId: null,
    renameCategory: null,
    renameSubcategory: null,
    renameItemCount: null,
    notifyPermissionDenied: false,
    dismissedRunId: readDismissedRunId(),
    pushNudge: readPushNudgeRecord(),
    toast: null,
    checkAnnouncement: '',
  }),
  actions: (set, get) => ({
    setQuarantine: (quarantine: { backupPath: string; at: string } | null) => {
      set({ ...get(), quarantine });
    },
    setOnboarding: (onboarding: 'auto' | OnboardingStep | null) => {
      const current = get();
      // Entering the flow snapshots the current topics by name, so the Topics
      // step can tell "added just now, via discovery" from "already had these"
      // (NEWS-146) and can name the pre-existing ones (NM-91).
      const entering = current.onboarding === null || current.onboarding === 'auto';
      const trulyEntering = entering && onboarding !== null && onboarding !== 'auto';
      const onboardingExistingNames = trulyEntering
        ? current.topics.map((t) => t.name)
        : current.onboardingExistingNames;
      // Snapshot the saved profiles at open too (NM-248), so Finish can tell the
      // interests picked *this run* from the ones already on the account.
      const onboardingProfilesAtOpen = trulyEntering
        ? [...current.settings.profiles]
        : current.onboardingProfilesAtOpen;
      set({ ...current, onboarding, onboardingExistingNames, onboardingProfilesAtOpen });
    },
    toggleOnboardingProfile: (id: string) => {
      const current = get();
      const chosen = current.onboardingProfiles.includes(id)
        ? current.onboardingProfiles.filter((p) => p !== id)
        : [...current.onboardingProfiles, id];
      set({ ...current, onboardingProfiles: chosen });
    },
    /**
     * Move within the profile picker's own pages (NEWS-383).
     *
     * Clamped rather than validated: the caller is a delegate reading the DOM,
     * and a page index out of range should land on a real page rather than
     * render nothing.
     */
    setOnboardingProfilePage: (page: number) => {
      const current = get();
      const last = PROFILE_PAGE_COUNT - 1;
      set({ ...current, onboardingProfilePage: Math.max(0, Math.min(last, page)) });
    },
    /**
     * Seed the picker from what is already saved (NEWS-383).
     *
     * Called on entering the step, not on load: onboarding can be reopened from
     * Settings by someone who ticked profiles months ago, and showing them an
     * empty grid would say their choices were lost.
     */
    seedOnboardingProfiles: (ids: readonly string[]) => {
      set({ ...get(), onboardingProfiles: [...ids], onboardingProfilePage: 0 });
    },
    toggleOnboardingTopic: (name: string) => {
      const current = get();
      const chosen = current.onboardingTopics.includes(name)
        ? current.onboardingTopics.filter((t) => t !== name)
        : [...current.onboardingTopics, name];
      set({ ...current, onboardingTopics: chosen });
    },
    setSettingsTab: (settingsTab: AppState['settingsTab']) => {
      set({ ...get(), settingsTab });
    },
    openExport: () => {
      // Always reopens on All + Markdown. A dialog that remembers the last
      // choice is a dialog that exports something different from what the last
      // press did, for a reason nothing on screen explains.
      set({ ...get(), export: { scope: 'all', topicId: null, format: 'md' } });
    },
    closeExport: () => {
      set({ ...get(), export: null });
    },
    /** Toggle the in-flight export state (NM-101) — the controls disable while true. */
    setExporting: (exporting: boolean) => {
      set({ ...get(), exporting });
    },
    patchExport: (patch: Partial<ExportChoice>) => {
      const current = get();
      if (current.export === null) return;
      const next = { ...current.export, ...patch };
      // Choosing "one topic" lands on the first one rather than on nothing, so
      // the option is usable the moment it is picked. Leaving it null would show
      // a picker with no selection and a disabled Export, which reads as broken
      // rather than as waiting.
      if (next.scope === 'topic' && next.topicId === null) next.topicId = current.topics[0]?.id ?? null;
      if (next.scope !== 'topic') next.topicId = null;
      set({ ...current, export: next });
    },
    setSettingsOpen: (settingsOpen: boolean) => {
      set({ ...get(), settingsOpen });
    },
    setAddTopicOpen: (addTopicOpen: boolean) => {
      set({ ...get(), addTopicOpen });
    },
    setSelection: (selectedTopicIds: string[]) => {
      set({ ...get(), selectedTopicIds });
    },
    // The four view-changing actions reset the feed page: a different view is a
    // fresh list, shown from the top (NEWS-62). They also collapse an expanded
    // story (NEWS-281) — the pane belongs to the list being read, and one left
    // open behind a filter change is state with nothing on screen to close it.
    setSolo: (soloTopicIds: string[]) => {
      set({ ...get(), soloTopicIds, feedLimit: FEED_PAGE, expandedItemId: null, topicPulse: null });
    },
    setCategoryFilter: (categoryFilter: AppState['categoryFilter']) => {
      set({ ...get(), categoryFilter, feedLimit: FEED_PAGE, expandedItemId: null, categoryPulse: null });
    },
    setSavedFilter: (savedFilter: boolean) => {
      set({ ...get(), savedFilter, feedLimit: FEED_PAGE, expandedItemId: null });
    },
    setSearchQuery: (searchQuery: string) => {
      set({ ...get(), searchQuery, feedLimit: FEED_PAGE, expandedItemId: null });
    },
    showMoreFeed: () => {
      set({ ...get(), feedLimit: get().feedLimit + FEED_PAGE });
    },
    /**
     * Open `id`'s detail pane, or close it if it is the one already open (NEWS-281).
     *
     * Opening a second story closes the first — see `expandedItemId` for why the
     * feed is an accordion rather than a set of open cards.
     */
    toggleItemExpanded: (id: string) => {
      const s = get();
      // `threadShowAll` belongs to the pane being opened, not to the reader, so
      // every open (and every close) starts capped again — see the field.
      set({ ...s, expandedItemId: s.expandedItemId === id ? null : id, threadShowAll: false });
    },
    /**
     * Collapse `id` specifically. A **no-op** when some other story — or none —
     * is expanded, so a caller acting on one card (flagging it off-topic) can't
     * close a pane the user opened on a different one.
     */
    collapseItem: (id: string) => {
      const s = get();
      if (s.expandedItemId !== id) return;
      set({ ...s, expandedItemId: null });
    },
    /** Collapse whatever is expanded (the Escape key). No-op when nothing is. */
    collapseExpandedItem: () => {
      const s = get();
      if (s.expandedItemId === null) return;
      set({ ...s, expandedItemId: null });
    },
    setFeed: (feed: { items: NewsItem[]; total: number; threads: Record<string, ThreadSummary> }) => {
      // `feedLoaded` latches true on the first feed answer (NM-249): only then does
      // an empty `feedItems` mean "this view genuinely has no stories" rather than
      // "the items request has not come back yet".
      set({ ...get(), feedLoaded: true, feedItems: feed.items, feedTotal: feed.total, threads: feed.threads });
    },
    setPulseSparklines: (pulseSparklines: AppState['pulseSparklines']) => {
      set({ ...get(), pulseSparklines });
    },
    setPulseSummaries: (topicPulse: PulseResp | null, categoryPulse: PulseResp | null) => {
      set({ ...get(), topicPulse, categoryPulse });
    },
    /**
     * Open the drill-down on `request` and start waiting for its answer. The
     * period control shows the requested period at once. The last answer stays
     * on screen only when it is for the **same** scope — a period change keeps
     * the figures under a busy state — and is cleared for any other, so a
     * different topic or section never shows the previous scope's numbers under
     * its own loading title (NM-HFXEYX).
     */
    openPulse: (request: PulseRequest) => {
      const s = get();
      const pending = normalizePulseRequest(request);
      const keep = s.pulseDetail !== null && samePulseScope(s.pulseDetail.scope, pending);
      set({
        ...s,
        pulseDetailOpen: true,
        pulseLoading: true,
        pulseDays: pending.days,
        pulsePending: pending,
        pulseDetail: keep ? s.pulseDetail : null,
      });
    },
    /**
     * A drill-down answer arrived. It never reopens the dialog: `openPulse` opened
     * it when the request began, and a reader who closed it while it loaded
     * (Escape, the close button, light dismiss) meant it (NM-NWFSDV). An answer
     * that is not for the pending request is dropped (NM-HFXEYX).
     */
    setPulseDetail: (pulseDetail: PulseResp) => {
      const s = get();
      const pending = s.pulsePending;
      if (pending === null || pending.days !== pulseDetail.days || !samePulseScope(pulseDetail.scope, pending)) return;
      set({ ...s, pulseDetail, pulseLoading: false, pulseDays: pulseDetail.days });
    },
    /**
     * The pending request failed. The period control goes back to the figures
     * still on screen, so it never claims a period that did not load.
     */
    failPulse: () => {
      const s = get();
      set({ ...s, pulseLoading: false, pulsePending: null, pulseDays: s.pulseDetail?.days ?? s.pulseDays });
    },
    closePulse: () => {
      set({ ...get(), pulseDetailOpen: false, pulseLoading: false });
    },
    /** Record what the thread route said (or that it is still being asked). */
    setThreadPane: (id: string, pane: ThreadPane) => {
      const s = get();
      set({ ...s, threadPanes: { ...s.threadPanes, [id]: pane } });
    },
    /** Lift the row cap on the open pane (NEWS-282). */
    showAllThread: () => {
      set({ ...get(), threadShowAll: true });
    },
    // Hold a just-flagged story (full data) so it can render collapsed even
    // though the server's normal-view page now excludes it (NEWS-76).
    addRecentlyFlagged: (item: NewsItem) => {
      const s = get();
      // Flagging collapses that story's pane (NEWS-281): the card becomes a
      // dimmed one-liner, which has no pane and no expander to close one with.
      const expandedItemId = s.expandedItemId === item.id ? null : s.expandedItemId;
      if (s.recentlyFlaggedItems.some((i) => i.id === item.id)) {
        if (expandedItemId !== s.expandedItemId) set({ ...s, expandedItemId });
        return;
      }
      set({ ...s, expandedItemId, recentlyFlaggedItems: [...s.recentlyFlaggedItems, { ...item, offTopic: true }] });
    },
    removeRecentlyFlagged: (id: string) => {
      const s = get();
      set({ ...s, recentlyFlaggedItems: s.recentlyFlaggedItems.filter((i) => i.id !== id) });
    },
    /**
     * Drop the client-only story state after every story has been cleared
     * (NEWS-291).
     *
     * `refreshState` cannot do this. `recentlyFlaggedItems` is an overlay the
     * *client* owns — full copies of stories flagged this session, merged into
     * the feed so a misclick stays undoable (NEWS-61) — so a server refresh
     * emptying `feedItems` leaves it untouched, and the overlay goes on rendering
     * rows whose database rows are gone. A clear that visibly leaves a story on
     * screen is the same bug this ticket is about, one layer up.
     *
     * Review mode goes with it: it shows only flagged stories, and there are
     * none, so staying in it would strand the user on a permanently empty feed
     * behind a banner.
     *
     * So does the expanded card (NEWS-281) and its thread state (NEWS-282):
     * `expandedItemId` would otherwise name a story that no longer exists. Every
     * other view change in this store already nulls it, and a clear is the most
     * drastic view change there is.
     *
     * `threadPanes` goes too. Its invalidation rule is the thread's **size** — a
     * thread that has grown is refetched — and a clear does not change a size, it
     * removes the thread entirely, so nothing about that rule would ever evict
     * these entries. No visible bug today, because story ids are UUIDs and a new
     * story cannot collide with a cached one; dropped because the store should not
     * go on holding fetched data about stories the user has deleted.
     */
    clearStoryOverlays: () => {
      set({
        ...get(),
        recentlyFlaggedItems: [],
        reviewTopicIds: [],
        feedLimit: FEED_PAGE,
        expandedItemId: null,
        threadShowAll: false,
        threadPanes: {},
      });
    },
    /**
     * The same rule as `clearStoryOverlays`, narrowed to one topic (NEWS-303).
     *
     * The per-topic clear — rename-with-clear, `PATCH /api/topics/:id` — had no
     * cleanup at all, so flagging a story and then clearing that topic left the
     * flagged row rendering over a feed whose database rows were gone. NEWS-273
     * fixed the app-wide clear and left this one; same bug, one topic's worth.
     *
     * **Not `clearStoryOverlays()` with a different name.** A per-topic clear
     * deletes exactly one topic's stories, so dropping every topic's overlay
     * would throw away rows the action did not touch: another topic's
     * just-flagged story, a review of a topic still holding flagged stories, an
     * expanded card belonging to somewhere else. Wiping state an action did not
     * invalidate is the same class of untruth as leaving state it did.
     *
     * Which is why membership is read from `feedItems` and the overlay rather
     * than taken from the server: both carry `topicId`, and the question here is
     * only *which of the stories the client is currently describing belonged to
     * that topic*. `feedLimit` is deliberately **not** reset — the user's "show
     * more" spans every topic, and one topic's clear is no reason to undo it.
     */
    clearStoryOverlaysForTopic: (topicId: string) => {
      const s = get();
      const gone = new Set(
        [...s.feedItems, ...s.recentlyFlaggedItems].filter((i) => i.topicId === topicId).map((i) => i.id),
      );
      const collapsing = s.expandedItemId !== null && gone.has(s.expandedItemId);
      set({
        ...s,
        recentlyFlaggedItems: s.recentlyFlaggedItems.filter((i) => i.topicId !== topicId),
        // Emptying this list is what leaves review mode, which is right when the
        // only topic under review has just lost every story it had to show.
        reviewTopicIds: s.reviewTopicIds.filter((id) => id !== topicId),
        expandedItemId: collapsing ? null : s.expandedItemId,
        threadShowAll: collapsing ? false : s.threadShowAll,
        threadPanes: Object.fromEntries(Object.entries(s.threadPanes).filter(([id]) => !gone.has(id))),
      });
    },
    setReviewTopicIds: (reviewTopicIds: string[]) => {
      set({ ...get(), reviewTopicIds, feedLimit: FEED_PAGE, expandedItemId: null });
    },
    openDiscover: () => {
      set({ ...get(), discover: emptyDiscover() });
    },
    closeDiscover: () => {
      set({ ...get(), discover: null });
    },
    /**
     * Patch the discovery pane.
     *
     * A patch rather than a whole-state setter because every caller changes one
     * or two fields of a six-field object, and a full replace is how the
     * in-flight `loading` flag or the `added` list gets silently reset by an
     * unrelated update. Ignored when the dialog is closed, so a response
     * arriving after the user closed it cannot reopen it.
     */
    patchDiscover: (patch: Partial<DiscoverState>) => {
      const current = get().discover;
      if (current === null) return;
      set({ ...get(), discover: { ...current, ...patch } });
    },
    openConfirm: (confirm: {
      message: string;
      title: string | null;
      confirmLabel: string;
      danger: boolean;
      holdSeconds: number;
      remaining: number;
    }) => {
      set({ ...get(), confirm });
    },
    /** Tick the danger-confirm countdown down one second (NM-101), never below 0. */
    tickConfirmCountdown: () => {
      const current = get().confirm;
      if (current === null || current.remaining <= 0) return;
      set({ ...get(), confirm: { ...current, remaining: current.remaining - 1 } });
    },
    closeConfirm: () => {
      set({ ...get(), confirm: null });
    },
    openRename: (renameTopicId: string) => {
      const current = get();
      const topic = current.topics.find((t) => t.id === renameTopicId);
      set({
        ...current,
        renameTopicId,
        renameItemCount: null,
        renameCategory: topic?.category ?? null,
        renameSubcategory: topic?.subcategory ?? null,
      });
    },
    setRenameItemCount: (renameItemCount: number) => {
      set({ ...get(), renameItemCount });
    },
    setRenameCategory: (renameCategory: string | null) => {
      // The subject always resets with the section: a subject from the previous
      // parent resolves to nothing, which is the same call FR-22.7 records for
      // the server side.
      set({ ...get(), renameCategory, renameSubcategory: null });
    },
    setRenameSubcategory: (renameSubcategory: string | null) => {
      set({ ...get(), renameSubcategory });
    },
    closeRename: () => {
      set({ ...get(), renameTopicId: null, renameItemCount: null });
    },
    setNotifyPermissionDenied: (notifyPermissionDenied: boolean) => {
      set({ ...get(), notifyPermissionDenied });
    },
    snoozePushNudge: (now: number = Date.now()) => {
      const pushNudge = dismissPushNudge(get().pushNudge, now);
      writePushNudgeRecord(pushNudge);
      set({ ...get(), pushNudge });
    },
    dismissRun: (dismissedRunId: string) => {
      writeDismissedRunId(dismissedRunId);
      set({ ...get(), dismissedRunId });
    },
    /** Update the polite check-progress announcement (NM-92). */
    setCheckAnnouncement: (checkAnnouncement: string) => {
      set({ ...get(), checkAnnouncement });
    },
    /**
     * Set the toast text with **no dismiss timer** (NEWS-141).
     *
     * Named `raw` because calling it directly is almost always a bug: the timer
     * lives in `showToast` in `app.tsx`, so a direct call puts a message on
     * screen and leaves it there. Use `showToast` unless you are it.
     */
    setToastRaw: (toast: ToastState | null) => {
      set({ ...get(), toast });
    },
    setTopicSort: (topicSort: TopicSort) => {
      if (typeof localStorage !== 'undefined') {
        try {
          localStorage.setItem(TOPIC_SORT_KEY, topicSort);
        } catch {
          // Storage unavailable — the choice still applies for this session.
        }
      }
      set({ ...get(), topicSort });
    },
    update: (partial: Partial<AppState>) => {
      set({ ...get(), ...partial });
    },
    setState: (state: StateResp) => {
      const current = get();
      const live = new Set(state.topics.map((t) => t.id));
      set({
        ...current,
        loaded: true,
        ...state,
        // Preserve the *identity* of an unchanged topic across polls (NM-214). The
        // rail is a keyed `each()`, which memoizes a row by its item's object
        // identity — but every 4-second `/api/state` poll parses fresh objects, so
        // the identity changed every time and the whole list re-reconciled (moving
        // nodes) even when nothing had changed. Reusing the previous object for a
        // structurally-identical topic lets `each()` skip it, so an idle app makes
        // zero DOM mutations.
        topics: carryIdentity(current.topics, state.topics),
        selectedTopicIds: current.selectedTopicIds.filter((id) => live.has(id)),
        soloTopicIds: current.soloTopicIds.filter((id) => live.has(id)),
      });
    },
    setError: (error: string | null) => {
      set({ ...get(), error });
    },
    /** Record the `/api/session` answer that drives the gate (NM-20). Clears any sign-in error. */
    setSession: (session: SessionResp) => {
      set({ ...get(), session, signInError: null });
    },
    setSignInError: (signInError: string | null) => {
      set({ ...get(), signInError });
    },
    /** Show or clear the "did you mean?" overlap steer for a typed add (NM-148/153). */
    setTopicSuggest: (topicSuggest: TopicSuggest | null) => {
      set({ ...get(), topicSuggest });
    },
    /** Insert an optimistic pending-topic placeholder while its create is in flight (NM-168). */
    addPendingTopic: (pending: PendingTopic) => {
      set({ ...get(), pendingTopics: [...get().pendingTopics, pending] });
    },
    /** Drop a pending placeholder once its create settles (NM-168). */
    removePendingTopic: (key: string) => {
      set({ ...get(), pendingTopics: get().pendingTopics.filter((p) => p.key !== key) });
    },
    /** Open the disambiguation sense picker for a topic (NM-163). */
    openSensePicker: (topicId: string) => {
      set({ ...get(), sensePickTopicId: topicId });
    },
    /** Close the disambiguation sense picker (NM-163). */
    closeSensePicker: () => {
      set({ ...get(), sensePickTopicId: null });
    },
    /** Expand/collapse the sidebar's Disabled group (NM-201). */
    toggleDisabledGroup: () => {
      set({ ...get(), disabledGroupOpen: !get().disabledGroupOpen });
    },
    /**
     * Open the capacity dialog in **disable** mode (NM-201): enabling `enableIds`
     * (of `kind`) is over the cap, so the reader picks enabled topics to disable.
     */
    openDisableCapacity: (kind: 'custom' | 'catalog', enableIds: string[]) => {
      set({ ...get(), capacityDialog: { mode: 'disable', kind, enableIds, picked: [] } });
    },
    /** Open the capacity dialog in **enable** mode — the upgrade prompt (NM-201). */
    openEnableCapacity: () => {
      set({ ...get(), capacityDialog: { mode: 'enable', kind: 'custom', enableIds: [], picked: [] } });
    },
    /** Close the capacity dialog (NM-201). */
    closeCapacity: () => {
      set({ ...get(), capacityDialog: null });
    },
    /** Open the upgrade checkout (NM-225/226), optionally pre-selecting a tier. */
    openCheckout: (plan: string | null = null) => {
      set({ ...get(), checkout: { plan, submitting: false, error: null } });
    },
    /** Patch the open checkout (pick a tier, set submitting, set/clear an error). */
    updateCheckout: (patch: Partial<CheckoutState>) => {
      const checkout = get().checkout;
      if (checkout === null) return;
      set({ ...get(), checkout: { ...checkout, ...patch } });
    },
    /** Close the upgrade checkout. */
    closeCheckout: () => {
      set({ ...get(), checkout: null });
    },
    /** Toggle a topic in the capacity dialog's ticked set (NM-201). */
    toggleCapacityPick: (topicId: string) => {
      const dialog = get().capacityDialog;
      if (dialog === null) return;
      const picked = dialog.picked.includes(topicId)
        ? dialog.picked.filter((id) => id !== topicId)
        : [...dialog.picked, topicId];
      set({ ...get(), capacityDialog: { ...dialog, picked } });
    },
    /** Replace the capacity dialog's ticked set (NM-201) — the select-all/none control. */
    setCapacityPicked: (picked: string[]) => {
      const dialog = get().capacityDialog;
      if (dialog === null) return;
      set({ ...get(), capacityDialog: { ...dialog, picked } });
    },
  }),
});
